import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const SECTIONS = [
  { id: 'summary', label: 'Overview', children: [] },
  { id: 'problem', label: 'Problem', children: [] },
  { id: 'discovery', label: 'Process', children: [] },
  { id: 'building-mvp', label: 'Foundation', children: [] },
  { id: 'impact', label: 'Impact', children: [] },
  { id: 'next-steps', label: 'Next Steps', children: [] },
  { id: 'learnings', label: 'Learnings', children: [] },
];

const ALL_IDS = SECTIONS.flatMap(s => [s.id, ...s.children.map(c => c.id)]);

const Container = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 4px;
`;

const NavLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  padding: 6px 10px;
  border-radius: 8px;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active }) => ($active ? 'var(--color-text)' : 'inherit')};
  opacity: ${({ $active }) => ($active ? 1 : 0.4)};
  background: ${({ $active }) => ($active ? 'var(--color-surface)' : 'transparent')};
  letter-spacing: 0;
  line-height: 1.4;
  transition: opacity 0.18s ease, background 0.18s ease, color 0.18s ease;
  white-space: nowrap;

  &:hover {
    opacity: 1;
    background: var(--color-surface);
    color: var(--color-text);
  }
`;

const Dot = styled.span`
  flex-shrink: 0;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--color-primary);
  transition: opacity 0.18s ease, transform 0.18s ease;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transform: ${({ $active }) => ($active ? 'scale(1)' : 'scale(0)')};
`;

const SubList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 2px 0 4px 18px;
  padding-left: 12px;
  border-left: 1.5px solid var(--color-border, rgba(0,0,0,0.12));
`;

const SubLink = styled.a`
  display: block;
  text-decoration: none;
  padding: 5px 8px;
  border-radius: 6px;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  color: ${({ $active }) => ($active ? 'var(--color-text)' : 'inherit')};
  opacity: ${({ $active }) => ($active ? 0.9 : 0.4)};
  letter-spacing: 0;
  line-height: 1.4;
  transition: opacity 0.15s ease, color 0.15s ease;
  white-space: nowrap;

  &:hover {
    opacity: 0.85;
    color: var(--color-text);
  }
`;

function getActiveId() {
  if (typeof window === 'undefined') return ALL_IDS[0];
  let best = ALL_IDS[0];
  let bestDist = Infinity;
  for (const id of ALL_IDS) {
    const el = document.getElementById(id);
    if (!el) continue;
    const dist = Math.abs(el.getBoundingClientRect().top - 120);
    if (dist < bestDist) { bestDist = dist; best = id; }
  }
  return best;
}

const OutlineDira = () => {
  const [activeId, setActiveId] = useState(ALL_IDS[0]);

  useEffect(() => {
    const onScroll = () => setActiveId(getActiveId());
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Container aria-label="Page outline">
      {SECTIONS.map(({ id, label, children }) => {
        const parentActive = activeId === id;
        const childActive = children.some(c => c.id === activeId);
        const highlight = parentActive || childActive;

        return (
          <div key={id}>
            <NavLink href={`#${id}`} $active={highlight}>
              <Dot $active={highlight} />
              {label}
            </NavLink>
            {children.length > 0 && highlight && (
              <SubList>
                {children.map(child => (
                  <SubLink
                    key={child.id}
                    href={`#${child.id}`}
                    $active={activeId === child.id}
                  >
                    {child.label}
                  </SubLink>
                ))}
              </SubList>
            )}
          </div>
        );
      })}
    </Container>
  );
};

export default OutlineDira;
